import { useMemo } from 'react';
import {
  Background,
  BackgroundVariant,
  MarkerType,
  ReactFlow,
  useReactFlow,
  type Connection,
  type EdgeTypes,
  type NodeTypes,
} from '@xyflow/react';
import { FallbackNode } from './FallbackNode';
import { SemanticNode } from '../nodes/components/SemanticNode';
import { getAllNodeDefinitions } from '../nodes/registry/node-registry';
import { NODE_DRAG_MIME } from '../nodes/palette/NodePalette';
import { GRID_SIZE, type EditorEdge } from '../domain/editor-graph';
import { useEditorStore } from '../state/editor-store';
import { useEditorShortcuts } from '../hooks/use-editor-shortcuts';
import { StandardEdge } from '../edges/components/StandardEdge';
import { ConditionalEdge } from '../edges/components/ConditionalEdge';
import { UnknownEdge } from '../edges/components/UnknownEdge';
import { validateConnection } from '../edges/domain/validate-connection';

const edgeTypes: EdgeTypes = {
  standard: StandardEdge,
  conditional: ConditionalEdge,
  unknown: UnknownEdge,
};

export function EditorCanvas() {
  const graph = useEditorStore((state) => state.graph);
  const applyNodeChanges = useEditorStore((state) => state.applyNodeChanges);
  const applyEdgeChanges = useEditorStore((state) => state.applyEdgeChanges);
  const connectNodes = useEditorStore((state) => state.connectNodes);
  const reconnectEdgeAction = useEditorStore((state) => state.reconnectEdgeAction);
  const beginDrag = useEditorStore((state) => state.beginDrag);
  const endDrag = useEditorStore((state) => state.endDrag);
  const setViewport = useEditorStore((state) => state.setViewport);
  const addNode = useEditorStore((state) => state.addNode);
  const { screenToFlowPosition } = useReactFlow();
  const nodeTypes = useMemo<NodeTypes>(
    () => ({
      fallback: FallbackNode,
      ...Object.fromEntries(
        getAllNodeDefinitions().map((definition) => [definition.type, SemanticNode]),
      ),
    }),
    [],
  );
  useEditorShortcuts();

  const isValidConnection = (connection: Connection | EditorEdge) => {
    if (!graph) return false;
    return validateConnection(graph, {
      source: connection.source,
      target: connection.target,
      sourceHandle: connection.sourceHandle,
      targetHandle: connection.targetHandle,
    }).valid;
  };

  if (!graph) return null;
  return (
    <div className="h-full min-w-0 flex-1 bg-bg-canvas" data-testid="editor-canvas">
      <ReactFlow
        nodes={graph.nodes}
        edges={graph.edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={applyNodeChanges}
        onEdgesChange={applyEdgeChanges}
        onConnect={(connection) => connectNodes(connection)}
        onReconnect={(oldEdge, connection) => reconnectEdgeAction(oldEdge, connection)}
        isValidConnection={isValidConnection}
        onNodeDragStart={beginDrag}
        onNodeDragStop={endDrag}
        onMoveEnd={(_, viewport) => setViewport(viewport)}
        onDragOver={(event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }}
        onDrop={(event) => {
          event.preventDefault();
          const type = event.dataTransfer.getData(NODE_DRAG_MIME);
          if (type) addNode(type, screenToFlowPosition({ x: event.clientX, y: event.clientY }));
        }}
        defaultViewport={graph.viewport}
        minZoom={0.2}
        maxZoom={2}
        snapToGrid
        snapGrid={[GRID_SIZE, GRID_SIZE]}
        selectionOnDrag
        panOnDrag={[1, 2]}
        multiSelectionKeyCode={['Shift', 'Meta', 'Control']}
        deleteKeyCode={null}
        nodesConnectable
        edgesFocusable
        reconnectRadius={12}
        connectionLineStyle={{ stroke: 'var(--df-text-brand)', strokeWidth: 1.75 }}
        defaultEdgeOptions={{
          type: 'standard',
          markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#c69032' },
        }}
        fitViewOptions={{ padding: 0.18 }}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={GRID_SIZE}
          size={1}
          color="rgba(198, 144, 50, 0.18)"
        />
      </ReactFlow>
    </div>
  );
}
